import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FiCheck,
  FiCopy,
  FiEdit2,
  FiEye,
  FiFileText,
  FiMessageCircle,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import api from "../services/api";
import AnamneseAIReviewCard from "./ai/AnamneseAIReviewCard";
import { listAnamneseReviews } from "../services/contextualAiApi";
import { formatCPF, formatPhone } from "../utils/validators";
import { useAuth } from "../contexts/AuthContext";

function formatDateTimeISOToPtBR(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString("pt-BR");
  } catch {
    return "-";
  }
}

function formatDateAnswerToPtBR(value) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-");
    return `${d}/${m}/${y}`;
  }
  const asDate = new Date(value);
  if (!isNaN(asDate.getTime())) return asDate.toLocaleDateString("pt-BR");
  return String(value ?? "—");
}

function getQuestionLabel(q) {
  return (
    q?.question_text ||
    q?.label ||
    q?.text ||
    q?.title ||
    q?.name ||
    q?.placeholder ||
    q?.question ||
    "Pergunta"
  );
}

function findQuestionInTemplate(template, answerItem, idx) {
  if (!template || !Array.isArray(template.questions)) return null;
  const { question_id, key, name, id } = answerItem || {};

  if (question_id || id) {
    const qid = (question_id || id).toString();
    const byId = template.questions.find(
      (q) => q.id === qid || q.question_id === qid
    );
    if (byId) return byId;
  }

  if (key || name) {
    const kk = (key || name).toString();
    const byKey = template.questions.find(
      (q) =>
        q.key === kk ||
        q.name === kk ||
        q.field === kk ||
        q.slug === kk ||
        q.id === kk
    );
    if (byKey) return byKey;
  }

  if (template.questions[idx]) return template.questions[idx];

  return null;
}

function formatAnswer(question, rawValue) {
  const qType = ((question?.type || question?.question_type || "") + "").toLowerCase();
  const qLabel = (question?.label || question?.question_text || "").toLowerCase();

  // Formata data
  if (qType === "date" || qType === "data" || qType === "birthday") {
    return formatDateAnswerToPtBR(rawValue);
  }

  // Formata CPF
  if (qLabel.includes('cpf') || qLabel.includes('c.p.f')) {
    return formatCPF(String(rawValue || ''));
  }

  // Formata telefone
  if (qLabel.includes('telefone') || qLabel.includes('celular') || qLabel.includes('tel') || qLabel.includes('contato')) {
    return formatPhone(String(rawValue || ''));
  }

  // Formata checkbox/multi
  if (qType === "checkbox" || qType === "multi" || Array.isArray(rawValue)) {
    try {
      const arr = Array.isArray(rawValue) ? rawValue : JSON.parse(rawValue || "[]");
      if (Array.isArray(arr)) return arr.join(", ");
    } catch {}
  }

  if (typeof rawValue === "boolean") return rawValue ? "Sim" : "Não";
  if (rawValue == null || rawValue === "") return "—";
  return String(rawValue);
}

const EmptyState = ({ message }) => (
  <div className="w-full py-10 text-center text-gray-500">{message}</div>
);

const RowActions = ({ onView, onEdit, onDelete, onAIReview, aiStatus, canUseAI, canManage }) => (
  <div className="flex flex-wrap items-center gap-2 justify-end">
    {canUseAI && <button
      type="button"
      className="px-2 py-1 text-sm rounded border border-purple-200 text-purple-700 hover:bg-purple-50 flex items-center gap-1"
      onClick={onAIReview}
      title="Revisar anamnese com IA"
    >
      <span aria-hidden="true">✨</span>
      {aiStatus === "stale"
        ? "Revisar novamente"
        : aiStatus === "accepted" || aiStatus === "edited"
        ? "IA revisada"
        : "Revisar com IA"}
    </button>}
    <button
      type="button"
      className="px-2 py-1 text-sm rounded border hover:bg-gray-50 flex items-center gap-1"
      onClick={onView}
      title="Visualizar"
    >
      <FiEye /> Ver
    </button>
    {canManage && <button
      type="button"
      className="px-2 py-1 text-sm rounded border hover:bg-gray-50 flex items-center gap-1"
      onClick={onEdit}
      title="Editar"
    >
      <FiEdit2 /> Editar
    </button>}
    {canManage && <button
      type="button"
      className="px-2 py-1 text-sm rounded border hover:bg-red-50 text-red-600 border-red-200 flex items-center gap-1"
      onClick={onDelete}
      title="Excluir"
    >
      <FiTrash2 /> Excluir
    </button>}
  </div>
);

export default function PatientAnamneseTab({
  patient,
  toast,
  hasWhatsAppModule = false,
}) {
  const { user } = useAuth();
  const permissions = new Set(user?.permissions || []);
  const canManage = permissions.has('anamnese.manage');
  const canSend = permissions.has('anamnese.send');
  const canUseAI = (user?.permissions || []).includes('ai.clinical_use');
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesMap, setTemplatesMap] = useState({});
  const [responses, setResponses] = useState([]);
  const [aiReviewsByResponse, setAiReviewsByResponse] = useState({});
  const [error, setError] = useState(null);

  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reviewingAI, setReviewingAI] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [templateAction, setTemplateAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Captura ID do paciente de forma robusta (evita "prontuarios")
  const uuidRe =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const routeId = params?.id || params?.patient_id || null;
  const safeRoutePatientId = routeId && uuidRe.test(routeId) ? routeId : null;

  const patientId = patient?.id || safeRoutePatientId || null;
  const showToast = (message, type = "success") => {
    if (toast?.showToast) {
      toast.showToast(message, type);
    } else if (type === "error") {
      window.alert(message);
    }
  };

  const getDefaultTemplate = () =>
    templates.find((template) => template?.is_default && (template?.is_active ?? true));

  const startTemplateAction = (action) => {
    const defaultTemplate = getDefaultTemplate();
    if (defaultTemplate) {
      executeTemplateAction(action, defaultTemplate);
      return;
    }
    setTemplateAction(action);
  };

  const ensurePublicLink = async (template) => {
    let publicLinkId = template?.public_link_id || template?.public_slug;
    if (!publicLinkId) {
      const response = await api.post(
        `/anamnese/templates/${template.id}/public-link`
      );
      publicLinkId =
        response.data?.public_link_id || response.data?.public_slug;
      setRefreshFlag((value) => value + 1);
    }
    if (!publicLinkId) {
      throw new Error("Não foi possível gerar o link público da anamnese.");
    }
    return `${window.location.origin}/anamnese/publico/${publicLinkId}`;
  };

  const executeTemplateAction = async (action, template) => {
    setTemplateAction(null);
    setActionLoading(true);
    try {
      if (action === "fill") {
        const params = new URLSearchParams({
          patient_id: patientId,
          template_id: template.id,
          patient_name: patient?.full_name || patient?.name || "",
          patient_cpf: patient?.cpf || "",
          patient_rg: patient?.rg || "",
          patient_email: patient?.email || "",
          patient_phone:
            patient?.phone ||
            patient?.mobile_phone ||
            patient?.whatsapp ||
            patient?.cellphone ||
            "",
          patient_birth_date: patient?.birth_date || "",
          patient_address: patient?.address || "",
        });
        window.location.href = `/anamnese?${params.toString()}`;
        return;
      }

      if (action === "copy") {
        const publicUrl = await ensurePublicLink(template);
        await navigator.clipboard.writeText(publicUrl);
        showToast("Link público da anamnese copiado.", "success");
        return;
      }

      if (action === "whatsapp") {
        const response = await api.post(
          `/anamnese/patients/${patientId}/send-public-link-whatsapp`,
          {},
          { params: { template_id: template.id } }
        );
        showToast(
          response.data?.message || "Link da anamnese enviado por WhatsApp.",
          "success"
        );
      }
    } catch (error) {
      console.error("Erro na ação de anamnese:", error);
      showToast(
        error?.response?.data?.detail ||
          error?.message ||
          "Não foi possível concluir a ação.",
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      if (!patientId) {
        setLoading(false);
        setResponses([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const tplRes = await api.get("/anamnese/templates");
        const tplList = Array.isArray(tplRes.data)
          ? tplRes.data
          : tplRes.data?.items || [];

        const map = {};
        tplList.forEach((t) => {
          if (t?.id) map[t.id] = t;
        });

        const respRes = await api.get("/anamnese/responses", {
          params: { patient_id: patientId, page: 1, page_size: 100, resolve_labels: true },
        });
        const respList = Array.isArray(respRes.data?.items)
          ? respRes.data.items
          : Array.isArray(respRes.data)
          ? respRes.data
          : [];

        // A indisponibilidade da IA não deve impedir o prontuário de carregar.
        const reviewList = canUseAI
          ? await listAnamneseReviews(patientId).catch(() => [])
          : [];
        const reviewMap = {};
        reviewList.forEach((review) => {
          const sourceId = String(review?.source_id || "");
          if (sourceId && !reviewMap[sourceId]) reviewMap[sourceId] = review;
        });

        if (!isMounted) return;
        setTemplates(tplList);
        setTemplatesMap(map);
        setResponses(respList);
        setAiReviewsByResponse(reviewMap);
      } catch (e) {
        console.error("Erro ao carregar anamneses:", e);
        if (isMounted) setError("Falha ao carregar anamneses do paciente.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [patientId, refreshFlag]);

  const rows = useMemo(() => {
    console.log('🔍 PatientAnamneseTab - Processando responses:', responses.length);
    return responses.map((r, rIdx) => {
      console.log(`\n📋 Response ${rIdx + 1}:`, {
        id: r?.id,
        template_id: r?.template_id,
        has_answers_resolved: !!r?.answers_resolved,
        answers_resolved_length: r?.answers_resolved?.length,
        has_answers: !!r?.answers,
        answers_type: Array.isArray(r?.answers) ? 'array' : typeof r?.answers
      });
      
      const template = r?.template_id ? templatesMap[r.template_id] : null;
      console.log('  📄 Template encontrado:', !!template, template ? {
        id: template.id,
        title: template.title,
        questions_count: template.questions?.length
      } : 'null');

      let answerArray = [];
      // Prioriza answers_resolved se disponível
      if (Array.isArray(r?.answers_resolved)) {
        answerArray = r.answers_resolved;
        console.log('  ✓ Usando answers_resolved:', answerArray.length, 'items');
        console.log('  📝 Primeiro item:', answerArray[0]);
      } else if (Array.isArray(r?.answers)) {
        answerArray = r.answers;
        console.log('  ✓ Usando answers (array):', answerArray.length, 'items');
      } else if (Array.isArray(r?.data)) {
        answerArray = r.data;
        console.log('  ✓ Usando data:', answerArray.length, 'items');
      } else if (r?.payload && Array.isArray(r.payload.answers)) {
        answerArray = r.payload.answers;
        console.log('  ✓ Usando payload.answers:', answerArray.length, 'items');
      } else if (r?.payload && Array.isArray(r.payload)) {
        answerArray = r.payload;
        console.log('  ✓ Usando payload:', answerArray.length, 'items');
      } else {
        console.log('  ⚠️ Nenhum array de respostas encontrado!');
      }

      const pairs = (answerArray || []).map((item, idx) => {
        const value =
          item?.answer ?? item?.value ?? item?.response ?? item?.val ?? item?.text ?? "";
        const qObj = findQuestionInTemplate(template, item, idx);
        const label =
          item?.label || item?.question_text || (qObj ? getQuestionLabel(qObj) : item?.question || "Pergunta");
        console.log(`    Pergunta ${idx + 1}: label="${label}", value="${value}"`, {
          item_label: item?.label,
          item_question_text: item?.question_text,
          qObj_found: !!qObj
        });
        const pretty = qObj ? formatAnswer(qObj, value) : String(value ?? "—");
        return { label, value: pretty };
      });

      return {
        id: r?.id || r?._id,
        created_at: r?.created_at || r?.submitted_at || r?.createdAt || r?.timestamp,
        origin: r?.origin || r?.source || (r?.is_external ? "Externo" : "Interno"),
        template_title:
          r?.template_title || template?.title || template?.name || "-",
        pairs,
        raw: r,
        template,
      };
    });
  }, [responses, templatesMap]);

  async function handleDelete(row) {
    if (!row?.id) return;
    if (!window.confirm("Confirma excluir esta anamnese?")) return;
    try {
      await api.delete(`/anamnese/responses/${row.id}`);
      setRefreshFlag((v) => v + 1);
    } catch (e) {
      console.error("Erro ao excluir anamnese:", e);
      alert("Falha ao excluir.");
    }
  }

  const handleAIAnalysisChange = (responseId, analysis) => {
    setAiReviewsByResponse((current) => ({
      ...current,
      [String(responseId)]: analysis,
    }));
  };

  if (!patientId) {
    return <EmptyState message="Selecione um paciente para visualizar as anamneses." />;
  }

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Anamnese do Paciente</h3>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canSend && <button
            type="button"
            className="px-3 py-1.5 text-sm rounded border flex items-center gap-2 hover:bg-gray-50"
            onClick={() => startTemplateAction("copy")}
            disabled={actionLoading || templates.length === 0}
          >
            <FiCopy />
            Copiar link
          </button>}
          {canSend && hasWhatsAppModule &&
            (patient?.phone ||
              patient?.mobile_phone ||
              patient?.whatsapp ||
              patient?.cellphone) && (
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-green-600 text-white flex items-center gap-2 hover:bg-green-700 disabled:opacity-60"
              onClick={() => startTemplateAction("whatsapp")}
              disabled={actionLoading || templates.length === 0}
            >
              <FiMessageCircle />
              Enviar por WhatsApp
            </button>
          )}
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded border flex items-center gap-2 hover:bg-gray-50"
            onClick={() => setRefreshFlag((v) => v + 1)}
            title="Atualizar"
          >
            <FiRefreshCw />
            Atualizar
          </button>
        </div>
      </div>

      {loading && <div className="py-6 text-gray-500">Carregando anamneses…</div>}
      {error && <div className="py-3 text-red-600">{error}</div>}

      {canUseAI && !loading && !error && rows.length > 0 && (
        <div className="mb-4">
          <AnamneseAIReviewCard
            patientId={patientId}
            responseId={rows[0].id}
            existingAnalysis={aiReviewsByResponse[String(rows[0].id)]}
            onAnalysisChange={(analysis) =>
              handleAIAnalysisChange(rows[0].id, analysis)
            }
            showToast={showToast}
          />
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="py-12 text-center bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-4">Paciente não possui anamnese cadastrada</p>
          <button
            onClick={() => startTemplateAction("fill")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            disabled={actionLoading || templates.length === 0}
          >
            Preencher Nova Anamnese
          </button>
        </div>
      )}

      {templateAction && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setTemplateAction(null)}
        >
          <div
            className="bg-white max-w-xl w-full rounded-lg p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold">Escolha o template</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Nenhum template foi definido como Anamnese padrão.
                </p>
              </div>
              <button
                type="button"
                className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                onClick={() => setTemplateAction(null)}
                aria-label="Fechar"
              >
                <FiX />
              </button>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {templates
                .filter((template) => template?.is_active ?? true)
                .map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className="w-full p-3 border rounded-lg text-left hover:border-blue-400 hover:bg-blue-50 flex items-start gap-3"
                    onClick={() => executeTemplateAction(templateAction, template)}
                  >
                    <FiFileText className="mt-0.5 text-blue-600" />
                    <span className="flex-1">
                      <span className="block font-medium text-gray-900">
                        {template.title || template.name}
                      </span>
                      {template.description && (
                        <span className="block text-sm text-gray-500 mt-1">
                          {template.description}
                        </span>
                      )}
                    </span>
                    <FiCheck className="mt-0.5 text-gray-400" />
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 w-48">Data/Hora</th>
                <th className="text-left px-3 py-2 w-28">Origem</th>
                <th className="text-left px-3 py-2 w-64">Template</th>
                <th className="text-left px-3 py-2">Conteúdo</th>
                <th className="text-right px-3 py-2 w-48">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2 align-top">
                    {formatDateTimeISOToPtBR(row.created_at)}
                  </td>
                  <td className="px-3 py-2 align-top">{row.origin || "-"}</td>
                  <td className="px-3 py-2 align-top">{row.template_title}</td>
                  <td className="px-3 py-2">
                    <ul className="space-y-1">
                      {row.pairs.map((p, i) => (
                        <li key={i}>
                          <span className="font-medium">{p.label}: </span>
                          <span>{p.value}</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <RowActions
                      canUseAI={canUseAI}
                      canManage={canManage}
                      onAIReview={() => setReviewingAI(row)}
                      aiStatus={aiReviewsByResponse[String(row.id)]?.status}
                      onView={() => setViewing(row)}
                      onEdit={() => setEditing(row)}
                      onDelete={() => handleDelete(row)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canUseAI && reviewingAI && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setReviewingAI(null)}
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-lg p-4"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold">Revisão assistida da anamnese</h4>
                <p className="text-sm text-gray-500">
                  {formatDateTimeISOToPtBR(reviewingAI.created_at)} · {reviewingAI.template_title}
                </p>
              </div>
              <button
                type="button"
                className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                onClick={() => setReviewingAI(null)}
              >
                Fechar
              </button>
            </div>
            <AnamneseAIReviewCard
              patientId={patientId}
              responseId={reviewingAI.id}
              existingAnalysis={aiReviewsByResponse[String(reviewingAI.id)]}
              onAnalysisChange={(analysis) =>
                handleAIAnalysisChange(reviewingAI.id, analysis)
              }
              showToast={showToast}
            />
          </div>
        </div>
      )}

      {viewing && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
          onClick={() => setViewing(null)}
        >
          <div
            className="bg-white max-w-3xl w-full rounded-lg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold">Anamnese</h4>
              <button
                type="button"
                className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                onClick={() => setViewing(null)}
              >
                Fechar
              </button>
            </div>

            <div className="mb-3 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Data/Hora</div>
                <div className="font-medium">
                  {formatDateTimeISOToPtBR(viewing.created_at)}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Origem</div>
                <div className="font-medium">{viewing.origin || "-"}</div>
              </div>
              <div>
                <div className="text-gray-500">Template</div>
                <div className="font-medium">{viewing.template_title}</div>
              </div>
            </div>

            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 w-1/3">Pergunta</th>
                    <th className="text-left px-3 py-2">Resposta</th>
                  </tr>
                </thead>
                <tbody>
                  {viewing.pairs.map((p, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2 align-top font-medium">{p.label}</td>
                      <td className="px-3 py-2 align-top">{p.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 text-right">
              <button
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                onClick={() => setViewing(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-white max-w-3xl w-full rounded-lg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold">Editar Anamnese</h4>
              <button
                type="button"
                className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                onClick={() => setEditing(null)}
              >
                Fechar
              </button>
            </div>

            <div className="mb-3 text-sm text-gray-600">
              Template: <span className="font-medium">{editing.template_title}</span>
              <br />
              Criada em:{" "}
              <span className="font-medium">
                {formatDateTimeISOToPtBR(editing.created_at)}
              </span>
            </div>

            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 w-1/3">Pergunta</th>
                    <th className="text-left px-3 py-2">Resposta</th>
                  </tr>
                </thead>
                <tbody>
                  {editing.pairs.map((p, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2 align-top font-medium">{p.label}</td>
                      <td className="px-3 py-2 align-top">{p.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                onClick={() => setEditing(null)}
              >
                Cancelar
              </button>
              <button
                className="px-3 py-1.5 text-sm border rounded bg-gray-100 text-gray-400 cursor-not-allowed"
                disabled
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
